import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type ShopProductStatus = 'draft' | 'active' | 'archived';
export type ShopProductKind = 'textile' | 'accessory' | 'limited_drop' | 'bundle';
export type ShopOrderStatus = 'cart' | 'pending_payment' | 'paid' | 'preordered' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';

export interface ShopProduct {
  id: string;
  slug: string;
  name: string;
  short_description: string | null;
  description: string | null;
  category: ShopProductKind;
  status: ShopProductStatus;
  badge: string | null;
  material: string | null;
  care_instructions: string | null;
  size_guide: string | null;
  shipping_info: string | null;
  returns_info: string | null;
  seo_title: string | null;
  seo_description: string | null;
  base_price_cents: number;
  compare_at_price_cents: number | null;
  featured: boolean;
  limited_edition: boolean;
  low_stock_threshold: number;
  drop_starts_at: string | null;
  drop_ends_at: string | null;
  sort_order: number;
  total_sales: number;
  created_at: string;
  updated_at: string;
  is_preorder?: boolean;
  preorder_ships_at?: string | null;
  preorder_max_quantity?: number | null;
  preorder_sold_count?: number;
  preorder_message?: string | null;
}

export interface ShopMedia {
  id: string;
  product_id: string;
  media_type: 'image' | 'video';
  url: string;
  alt_text: string | null;
  sort_order: number;
  is_primary: boolean;
}

export interface ShopVariant {
  id: string;
  product_id: string;
  sku: string | null;
  name: string;
  size: string | null;
  color: string | null;
  price_cents: number;
  compare_at_price_cents: number | null;
  stock_quantity: number;
  reserved_quantity: number;
  weight_grams: number | null;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface ShopReview {
  id: string;
  product_id: string;
  user_id: string;
  order_id: string | null;
  rating: number;
  title: string | null;
  comment: string | null;
  status: 'pending' | 'published' | 'rejected';
  created_at: string;
}

export interface ShopCartItemView {
  id: string;
  cart_id: string;
  product_id: string;
  variant_id: string;
  quantity: number;
  unit_price_cents: number;
  product: ShopProduct | null;
  variant: ShopVariant | null;
  line_total_cents: number;
  image_url: string | null;
}

export interface ShopCartView {
  id: string;
  user_id: string;
  status: string;
  currency: string;
  coupon_code: string | null;
  items: ShopCartItemView[];
  subtotal_cents: number;
}

export interface ShopProductDetail {
  product: ShopProduct;
  media: ShopMedia[];
  variants: ShopVariant[];
  relatedProducts: ShopProduct[];
  reviews: ShopReview[];
  relatedProductsStock: Map<string, number>;
  relatedDefaultVariants: Map<string, ShopVariant>;
  relatedPrimaryImage: Map<string, string>;
}

export interface ShopOrder {
  id: string;
  user_id: string | null;
  order_number: string;
  status: ShopOrderStatus;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  customer_email: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  shipping_name: string | null;
  shipping_city: string | null;
  shipping_country: string | null;
  created_at: string;
  updated_at: string;
  shipped_at: string | null;
}

export interface ShopOrderItem {
  id: string;
  order_id: string;
  product_id: string | null;
  variant_id: string | null;
  product_name: string;
  variant_name: string | null;
  sku: string | null;
  unit_price_cents: number;
  quantity: number;
  line_total_cents: number;
}

export function getVariantAvailableStock(variant: ShopVariant) {
  return Math.max(variant.stock_quantity - variant.reserved_quantity, 0);
}

export function isPreorderProduct(product?: Pick<ShopProduct, 'is_preorder'> | null): boolean {
  return !!product?.is_preorder;
}

export function getPreorderRemainingQuota(product?: Pick<ShopProduct, 'is_preorder' | 'preorder_max_quantity' | 'preorder_sold_count'> | null): number | null {
  if (!product?.is_preorder) return null;
  if (product.preorder_max_quantity == null) return null; // unlimited
  return Math.max(0, product.preorder_max_quantity - (product.preorder_sold_count || 0));
}

export function isPreorderSoldOut(product?: Pick<ShopProduct, 'is_preorder' | 'preorder_max_quantity' | 'preorder_sold_count'> | null): boolean {
  const remaining = getPreorderRemainingQuota(product);
  return remaining !== null && remaining <= 0;
}

export function useShopProducts(filters?: { featured?: boolean; category?: ShopProductKind | 'all'; search?: string }) {
  return useQuery({
    queryKey: ['shop-products', filters],
    queryFn: async () => {
      let query = supabase
        .from('shop_products')
        .select('*')
        .eq('status', 'active')
        .order('featured', { ascending: false })
        .order('sort_order', { ascending: true });

      if (filters?.featured) query = query.eq('featured', true);
      if (filters?.category && filters.category !== 'all') query = query.eq('category', filters.category);
      if (filters?.search) query = query.ilike('name', `%${filters.search}%`);

      const { data, error } = await query;
      if (error) throw error;
      const products = (data || []) as ShopProduct[];
      if (products.length === 0) return [] as (ShopProduct & { primary_image_url: string | null })[];

      const ids = products.map((p) => p.id);
      const { data: media } = await supabase
        .from('shop_product_media')
        .select('product_id, url, is_primary, sort_order, media_type')
        .in('product_id', ids)
        .eq('media_type', 'image')
        .order('is_primary', { ascending: false })
        .order('sort_order', { ascending: true });

      const imageByProduct = new Map<string, string>();
      for (const m of (media || []) as Array<{ product_id: string; url: string }>) {
        if (!imageByProduct.has(m.product_id)) imageByProduct.set(m.product_id, m.url);
      }

      return products.map((p) => ({
        ...p,
        primary_image_url: imageByProduct.get(p.id) ?? null,
      })) as (ShopProduct & { primary_image_url: string | null })[];
    },
  });
}

export function useShopProduct(slug?: string) {
  return useQuery({
    queryKey: ['shop-product-detail', slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data: product, error: productError } = await supabase
        .from('shop_products')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'active')
        .single();

      if (productError) throw productError;

      const [{ data: media }, { data: variants }, { data: relations }, { data: reviews }] = await Promise.all([
        supabase.from('shop_product_media').select('*').eq('product_id', product.id).order('sort_order', { ascending: true }),
        supabase.from('shop_variants').select('*').eq('product_id', product.id).eq('is_active', true).order('sort_order', { ascending: true }),
        supabase.from('shop_related_products').select('*').eq('product_id', product.id).order('sort_order', { ascending: true }),
        supabase.from('shop_reviews').select('*').eq('product_id', product.id).eq('status', 'published').order('created_at', { ascending: false }),
      ]);

      const relatedIds = (relations || []).map((item: any) => item.related_product_id);
      let relatedProducts: ShopProduct[] = [];

      if (relatedIds.length > 0) {
        const { data } = await supabase.from('shop_products').select('*').in('id', relatedIds).eq('status', 'active');
        const mapped = new Map((data || []).map((item: any) => [item.id, item]));
        relatedProducts = relatedIds.map((id) => mapped.get(id)).filter(Boolean) as ShopProduct[];
      }

      // Fetch stock, default variants and primary images for related products
      const relatedProductsStock = new Map<string, number>();
      const relatedDefaultVariants = new Map<string, ShopVariant>();
      const relatedPrimaryImage = new Map<string, string>();
      if (relatedProducts.length > 0) {
        const rpIds = relatedProducts.map((p) => p.id);
        const [{ data: rpVariants }, { data: rpMedia }] = await Promise.all([
          supabase
            .from('shop_variants')
            .select('*')
            .in('product_id', rpIds)
            .eq('is_active', true)
            .order('is_default', { ascending: false })
            .order('sort_order', { ascending: true }),
          supabase
            .from('shop_product_media')
            .select('product_id, url, is_primary, sort_order, media_type')
            .in('product_id', rpIds)
            .eq('media_type', 'image')
            .order('is_primary', { ascending: false })
            .order('sort_order', { ascending: true }),
        ]);
        for (const rp of relatedProducts) {
          const vs = ((rpVariants || []) as ShopVariant[]).filter((v) => v.product_id === rp.id);
          const total = vs.reduce((sum, v) => sum + Math.max(0, (v.stock_quantity || 0) - (v.reserved_quantity || 0)), 0);
          relatedProductsStock.set(rp.id, total);
          const def = vs.find((v) => v.is_default && getVariantAvailableStock(v) > 0) || vs.find((v) => getVariantAvailableStock(v) > 0) || vs[0];
          if (def) relatedDefaultVariants.set(rp.id, def);
          const img = ((rpMedia || []) as Array<{ product_id: string; url: string }>).find((m) => m.product_id === rp.id);
          if (img) relatedPrimaryImage.set(rp.id, img.url);
        }
      }

      return {
        product: product as ShopProduct,
        media: ((media || []) as ShopMedia[]).sort((a, b) => Number(b.is_primary) - Number(a.is_primary) || a.sort_order - b.sort_order),
        variants: (variants || []) as ShopVariant[],
        relatedProducts,
        reviews: (reviews || []) as ShopReview[],
        relatedProductsStock,
        relatedDefaultVariants,
        relatedPrimaryImage,
      } satisfies ShopProductDetail;
    },
  });
}

async function getOrCreateActiveCart(userId: string) {
  const { data: existing, error: existingError } = await supabase
    .from('shop_carts')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  if (existingError) throw existingError;
  if (existing) return existing as any;

  const { data: created, error: createError } = await supabase
    .from('shop_carts')
    .insert({ user_id: userId, status: 'active', currency: 'EUR' })
    .select('*')
    .single();

  if (createError) throw createError;
  return created as any;
}

export function useShopCart() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['shop-cart', user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return null;

      const cart = await getOrCreateActiveCart(user.id);
      const { data: items, error: itemError } = await supabase
        .from('shop_cart_items')
        .select('*')
        .eq('cart_id', cart.id)
        .order('created_at', { ascending: true });

      if (itemError) throw itemError;

      const productIds = [...new Set((items || []).map((item: any) => item.product_id))];
      const variantIds = [...new Set((items || []).map((item: any) => item.variant_id))];

      const [{ data: products }, { data: variants }, { data: media }] = await Promise.all([
        productIds.length ? supabase.from('shop_products').select('*').in('id', productIds) : Promise.resolve({ data: [] as any[] }),
        variantIds.length ? supabase.from('shop_variants').select('*').in('id', variantIds) : Promise.resolve({ data: [] as any[] }),
        productIds.length
          ? supabase
              .from('shop_product_media')
              .select('product_id, url, is_primary, sort_order, media_type')
              .in('product_id', productIds)
              .eq('media_type', 'image')
              .order('is_primary', { ascending: false })
              .order('sort_order', { ascending: true })
          : Promise.resolve({ data: [] as any[] }),
      ]);

      const productMap = new Map((products || []).map((item: any) => [item.id, item as ShopProduct]));
      const variantMap = new Map((variants || []).map((item: any) => [item.id, item as ShopVariant]));
      const imageMap = new Map<string, string>();
      ((media || []) as Array<{ product_id: string; url: string }>).forEach((m) => {
        if (!imageMap.has(m.product_id)) imageMap.set(m.product_id, m.url);
      });

      const normalizedItems = ((items || []) as any[]).map((item) => ({
        ...item,
        product: productMap.get(item.product_id) || null,
        variant: variantMap.get(item.variant_id) || null,
        line_total_cents: item.unit_price_cents * item.quantity,
        image_url: imageMap.get(item.product_id) || null,
      })) as ShopCartItemView[];

      return {
        ...(cart as any),
        items: normalizedItems,
        subtotal_cents: normalizedItems.reduce((sum, item) => sum + item.line_total_cents, 0),
      } as ShopCartView;
    },
  });
}

export function useAddToShopCart() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ product, variant, quantity }: { product: ShopProduct; variant: ShopVariant; quantity: number }) => {
      if (!user) throw new Error('AUTH_REQUIRED');
      const cart = await getOrCreateActiveCart(user.id);

      const { data: existing } = await supabase
        .from('shop_cart_items')
        .select('*')
        .eq('cart_id', cart.id)
        .eq('variant_id', variant.id)
        .maybeSingle();

      const nextQuantity = (existing?.quantity || 0) + quantity;
      if (!isPreorderProduct(product)) {
        if (nextQuantity > getVariantAvailableStock(variant)) {
          throw new Error('STOCK_EXCEEDED');
        }
      } else {
        // Preorder: check quota instead of stock
        if (isPreorderSoldOut(product)) {
          throw new Error('PREORDER_SOLD_OUT');
        }
        const remaining = getPreorderRemainingQuota(product);
        if (remaining !== null && nextQuantity > remaining) {
          throw new Error('PREORDER_QUOTA_EXCEEDED');
        }
      }

      if (existing) {
        const { error } = await supabase
          .from('shop_cart_items')
          .update({ quantity: nextQuantity, unit_price_cents: variant.price_cents })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('shop_cart_items').insert({
          cart_id: cart.id,
          product_id: product.id,
          variant_id: variant.id,
          quantity,
          unit_price_cents: variant.price_cents,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-cart'] });
    },
  });
}

export function useUpdateShopCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: string; quantity: number }) => {
      if (quantity <= 0) {
        const { error } = await supabase.from('shop_cart_items').delete().eq('id', itemId);
        if (error) throw error;
        return;
      }

      const { error } = await supabase.from('shop_cart_items').update({ quantity }).eq('id', itemId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-cart'] });
    },

  });
}

export function useCreateShopCheckout() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      cart,
      shipping,
      couponCode,
    }: {
      cart: ShopCartView;
      shipping: {
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        address1: string;
        address2?: string;
        postalCode: string;
        city: string;
        country: string;
      };
      couponCode?: string | null;
    }) => {
      if (!user) throw new Error('AUTH_REQUIRED');
      if (!cart.items.length) throw new Error('EMPTY_CART');

      const subtotal = cart.items.reduce((sum, item) => sum + item.line_total_cents, 0);

      // Re-validate coupon server-side to prevent tampering
      let discountCents = 0;
      let freeShipping = false;
      let couponId: string | null = null;
      let normalizedCode: string | null = null;
      if (couponCode && couponCode.trim()) {
        const { data: cdata, error: cerr } = await supabase.rpc('validate_shop_coupon' as any, {
          _code: couponCode,
          _subtotal_cents: subtotal,
        });
        if (cerr) throw new Error('INVALID_COUPON');
        const row = Array.isArray(cdata) ? (cdata as any[])[0] : (cdata as any);
        if (row) {
          discountCents = Number(row.discount_cents) || 0;
          freeShipping = !!row.free_shipping;
          couponId = row.coupon_id || null;
          normalizedCode = row.code || null;
        }
      }

      const afterDiscount = Math.max(subtotal - discountCents, 0);
      const shippingCents = freeShipping ? 0 : (afterDiscount >= 9000 ? 0 : 590);
      const total = afterDiscount + shippingCents;

      const { data: order, error: orderError } = await supabase
        .from('shop_orders')
        .insert([{
          order_number: '',
          user_id: user.id,
          status: 'pending_payment',
          currency: 'EUR',
          subtotal_cents: subtotal,
          discount_cents: discountCents,
          shipping_cents: shippingCents,
          tax_cents: 0,
          total_cents: total,
          customer_email: shipping.email,
          customer_first_name: shipping.firstName,
          customer_last_name: shipping.lastName,
          shipping_name: `${shipping.firstName} ${shipping.lastName}`.trim(),
          shipping_address_line_1: shipping.address1,
          shipping_address_line_2: shipping.address2 || null,
          shipping_postal_code: shipping.postalCode,
          shipping_city: shipping.city,
          shipping_country: shipping.country,
          shipping_phone: shipping.phone || null,
          coupon_code: normalizedCode,
          coupon_id: couponId,
        }] as any)
        .select('*')
        .single();

      if (orderError) throw orderError;

      const orderItemsPayload = cart.items.map((item) => ({
        order_id: order.id,
        product_id: item.product_id,
        variant_id: item.variant_id,
        product_name: item.product?.name || 'Produit boutique',
        variant_name: item.variant?.name || null,
        sku: item.variant?.sku || null,
        unit_price_cents: item.unit_price_cents,
        quantity: item.quantity,
        line_total_cents: item.line_total_cents,
        is_preorder: !!item.product?.is_preorder,
        preorder_ships_at: item.product?.is_preorder ? (item.product?.preorder_ships_at ?? null) : null,
      }));

      const { error: orderItemsError } = await supabase.from('shop_order_items').insert(orderItemsPayload as any);
      if (orderItemsError) throw orderItemsError;

      const redirectUrl = `${window.location.origin}/paiement/callback?shop_order_id=${order.id}`;
      const { data, error } = await supabase.functions.invoke('create-mollie-payment', {
        body: {
          shop_order_id: order.id,
          amount_cents: total,
          event_title: 'Boutique officielle Kourse',
          user_id: user.id,
          redirect_url: redirectUrl,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Non-blocking : si la mise à jour du panier échoue (RLS, race condition…),
      // on continue quand même pour rediriger l'utilisateur vers Mollie.
      try {
        const { error: cartError } = await supabase
          .from('shop_carts')
          .update({ status: 'converted' } as any)
          .eq('id', cart.id);
        if (cartError) console.warn('Cart status update failed (non-blocking):', cartError.message);
      } catch (err) {
        console.warn('Cart status update threw (non-blocking):', err);
      }

      const result = data as { checkout_url: string; payment_id: string; mollie_payment_id: string };

      // Redirection immédiate dans la mutationFn pour garantir le départ
      // même si quelque chose échoue plus tard dans onSuccess.
      if (result?.checkout_url && typeof window !== 'undefined') {
        window.location.assign(result.checkout_url);
      }

      return result;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['shop-cart'] });
      if (data?.checkout_url && typeof window !== 'undefined' && !window.location.href.includes('mollie.com')) {
        window.location.href = data.checkout_url;
      }
    },
    onError: (error: Error) => {
      console.error('Checkout error:', error.message);
    },
  });
}

export function useAdminShopProducts() {
  return useQuery({
    queryKey: ['admin-shop-products'],
    queryFn: async () => {
      const { data, error } = await supabase.from('shop_products').select('*').order('updated_at', { ascending: false });
      if (error) throw error;
      return (data || []) as ShopProduct[];
    },
  });
}

export function useAdminShopVariants() {
  return useQuery({
    queryKey: ['admin-shop-variants'],
    queryFn: async () => {
      const { data, error } = await supabase.from('shop_variants').select('*').order('updated_at', { ascending: false });
      if (error) throw error;
      return (data || []) as ShopVariant[];
    },
  });
}

export function useAdminShopOrders() {
  return useQuery({
    queryKey: ['admin-shop-orders'],
    queryFn: async () => {
      const { data: orders, error } = await supabase.from('shop_orders').select('*').order('created_at', { ascending: false });
      if (error) throw error;

      const orderIds = (orders || []).map((order: any) => order.id);
      const { data: items } = orderIds.length
        ? await supabase.from('shop_order_items').select('*').in('order_id', orderIds)
        : { data: [] as any[] };

      const itemGroups = new Map<string, ShopOrderItem[]>();
      (items || []).forEach((item: any) => {
        itemGroups.set(item.order_id, [...(itemGroups.get(item.order_id) || []), item as ShopOrderItem]);
      });

      return (orders || []).map((order: any) => ({ ...order, items: itemGroups.get(order.id) || [] })) as Array<ShopOrder & { items: ShopOrderItem[] }>;
    },
  });
}

export function useUpsertShopProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Partial<ShopProduct> & { id?: string; name: string; slug: string }) => {
      if (payload.id) {
        const { data, error } = await supabase.from('shop_products').update(payload).eq('id', payload.id).select('*').single();
        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase.from('shop_products').insert(payload).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shop-products'] });
      queryClient.invalidateQueries({ queryKey: ['shop-products'] });
      toast.success('Produit boutique enregistré');
    },
    onError: () => toast.error('Impossible d’enregistrer le produit'),
  });
}

export function useUpsertShopVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Partial<ShopVariant> & { id?: string; product_id: string; name: string; price_cents: number }) => {
      if (payload.id) {
        const { data, error } = await supabase.from('shop_variants').update(payload).eq('id', payload.id).select('*').single();
        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase.from('shop_variants').insert(payload).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shop-variants'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Variante enregistrée');
    },
    onError: () => toast.error('Impossible d’enregistrer la variante'),
  });
}

export function useUpdateShopOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, status, note }: { orderId: string; status: ShopOrderStatus; note?: string }) => {
      const { error } = await supabase.from('shop_orders').update({ status }).eq('id', orderId);
      if (error) throw error;

      const { error: historyError } = await supabase.from('shop_order_status_history').insert({
        order_id: orderId,
        status,
        note: note || null,
      });
      if (historyError) throw historyError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shop-orders'] });
      toast.success('Statut de commande mis à jour');
    },
    onError: () => toast.error('Impossible de mettre à jour la commande'),
  });
}

export function useShopInsights(products: ShopProduct[] = [], orders: Array<ShopOrder & { items?: ShopOrderItem[] }> = []) {
  return useMemo(() => {
    const activeProducts = products.filter((item) => item.status === 'active').length;
    const paidOrders = orders.filter((item) => item.status === 'paid' || item.status === 'processing' || item.status === 'shipped' || item.status === 'delivered');
    const revenue = paidOrders.reduce((sum, order) => sum + order.total_cents, 0);
    const avgOrderValue = paidOrders.length ? Math.round(revenue / paidOrders.length) : 0;

    return {
      activeProducts,
      paidOrders: paidOrders.length,
      revenue,
      avgOrderValue,
    };
  }, [orders, products]);
}

// ---- Shop Media hooks (admin) ----

export function useShopMedia(productId?: string) {
  return useQuery({
    queryKey: ['shop-media', productId],
    enabled: !!productId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_product_media')
        .select('*')
        .eq('product_id', productId!)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data || []) as ShopMedia[];
    },
  });
}

export function useUploadShopMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, file }: { productId: string; file: File }) => {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `products/${productId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('merch-images')
        .upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('merch-images').getPublicUrl(uploadData.path);

      // Check if there are existing media to determine is_primary
      const { count } = await supabase.from('shop_product_media').select('id', { count: 'exact', head: true }).eq('product_id', productId);
      const isPrimary = (count || 0) === 0;

      const { data, error } = await supabase.from('shop_product_media').insert({
        product_id: productId,
        media_type: 'image',
        url: urlData.publicUrl,
        alt_text: file.name,
        sort_order: (count || 0),
        is_primary: isPrimary,
      } as any).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-media'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Image uploadée');
    },
    onError: () => toast.error("Erreur lors de l'upload"),
  });
}

export function useDeleteShopMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ mediaId, url }: { mediaId: string; url: string }) => {
      // Delete from storage
      try {
        const pathMatch = url.match(/\/storage\/v1\/object\/public\/merch-images\/(.+)/);
        if (pathMatch) {
          await supabase.storage.from('merch-images').remove([decodeURIComponent(pathMatch[1])]);
        }
      } catch { /* ignore storage errors */ }

      const { error } = await supabase.from('shop_product_media').delete().eq('id', mediaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-media'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Image supprimée');
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });
}

export function useSetPrimaryMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ mediaId, productId }: { mediaId: string; productId: string }) => {
      // Reset all to non-primary
      await supabase.from('shop_product_media').update({ is_primary: false } as any).eq('product_id', productId);
      // Set selected as primary
      const { error } = await supabase.from('shop_product_media').update({ is_primary: true } as any).eq('id', mediaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-media'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Image principale définie');
    },
    onError: () => toast.error("Erreur"),
  });
}

// ---- Related Products (Cross-sell) hooks (admin) ----

export interface RelatedProductRow {
  id: string;
  product_id: string;
  related_product_id: string;
  relation_type: string;
  sort_order: number;
  related_product?: ShopProduct;
  primary_image_url?: string;
}

export function useAdminRelatedProducts(productId?: string) {
  return useQuery({
    queryKey: ['admin-related-products', productId],
    enabled: !!productId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_related_products')
        .select('*')
        .eq('product_id', productId!)
        .order('sort_order', { ascending: true });
      if (error) throw error;

      const rows = (data || []) as RelatedProductRow[];
      if (!rows.length) return rows;

      const relIds = rows.map((r) => r.related_product_id);
      const [{ data: products }, { data: media }] = await Promise.all([
        supabase.from('shop_products').select('*').in('id', relIds),
        supabase.from('shop_product_media').select('product_id, url, is_primary').in('product_id', relIds).eq('media_type', 'image').order('is_primary', { ascending: false }).order('sort_order', { ascending: true }),
      ]);

      const prodMap = new Map((products || []).map((p: any) => [p.id, p as ShopProduct]));
      const imgMap = new Map<string, string>();
      (media || []).forEach((m: any) => { if (!imgMap.has(m.product_id)) imgMap.set(m.product_id, m.url); });

      return rows.map((r) => ({ ...r, related_product: prodMap.get(r.related_product_id), primary_image_url: imgMap.get(r.related_product_id) }));
    },
  });
}

export function useAddRelatedProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, relatedProductId, relationType, sortOrder }: { productId: string; relatedProductId: string; relationType: string; sortOrder: number }) => {
      const { error } = await supabase.from('shop_related_products').insert({
        product_id: productId,
        related_product_id: relatedProductId,
        relation_type: relationType,
        sort_order: sortOrder,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-related-products'] });
      queryClient.invalidateQueries({ queryKey: ['admin-shop-products'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Produit associé ajouté');
    },
    onError: () => toast.error('Erreur lors de l\'ajout'),
  });
}

export function useRemoveRelatedProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('shop_related_products').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-related-products'] });
      queryClient.invalidateQueries({ queryKey: ['admin-shop-products'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Produit associé retiré');
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });
}

// ---- Variant management hooks (admin) ----

export function useDeleteShopVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variantId: string) => {
      const { error } = await supabase.from('shop_variants').delete().eq('id', variantId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shop-variants'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Variante supprimée');
    },
    onError: () => toast.error('Impossible de supprimer la variante'),
  });
}

export function useToggleDefaultVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ variantId, productId }: { variantId: string; productId: string }) => {
      await supabase.from('shop_variants').update({ is_default: false } as any).eq('product_id', productId);
      const { error } = await supabase.from('shop_variants').update({ is_default: true } as any).eq('id', variantId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shop-variants'] });
      queryClient.invalidateQueries({ queryKey: ['shop-product-detail'] });
      toast.success('Variante par défaut mise à jour');
    },
    onError: () => toast.error('Erreur'),
  });
}

// ---- Shop Reviews hooks ----

export function useShopReviews(productId?: string) {
  return useQuery({
    queryKey: ['shop-reviews', productId],
    enabled: !!productId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_reviews')
        .select('*')
        .eq('product_id', productId!)
        .eq('status', 'published')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as ShopReview[];
    },
  });
}

export function useSubmitShopReview() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ productId, orderId, rating, title, comment }: { productId: string; orderId?: string; rating: number; title?: string; comment?: string }) => {
      if (!user) throw new Error('AUTH_REQUIRED');
      const { data, error } = await supabase.from('shop_reviews').insert({
        product_id: productId,
        user_id: user.id,
        order_id: orderId || null,
        rating,
        title: title || null,
        comment: comment || null,
        status: 'pending',
      } as any).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-reviews'] });
    },
  });
}

// ---- Shop Coupon validation ----

export function useValidateShopCoupon() {
  return useMutation({
    mutationFn: async ({ code, subtotalCents }: { code: string; subtotalCents: number }) => {
      const { data, error } = await supabase.rpc('validate_shop_coupon' as any, {
        _code: code,
        _subtotal_cents: subtotalCents,
      });

      if (error) {
        const msg = (error.message || '').toUpperCase();
        const known = ['INVALID_CODE', 'NOT_YET_ACTIVE', 'EXPIRED', 'MAX_USES_REACHED', 'MIN_NOT_MET'];
        const found = known.find((k) => msg.includes(k));
        throw new Error(found || 'INVALID_CODE');
      }
      const row = Array.isArray(data) ? (data as any[])[0] : (data as any);
      if (!row) throw new Error('INVALID_CODE');

      return {
        coupon: { id: row.coupon_id, code: row.code, coupon_type: row.coupon_type },
        couponId: row.coupon_id as string,
        discountCents: row.discount_cents as number,
        freeShipping: !!row.free_shipping,
      };
    },
  });
}